/**
 * Color Constants
 *
 * Centralized color definitions for the Transportation Safety Platform.
 * All colors should be imported from this file to ensure consistency.
 *
 * Palette: Washi design system (approved 2026-09-30) — warm paper, sumi ink,
 * vermilion/moss/ochre accents. Values mirror the @theme scales in
 * src/index.css; keep the two in sync when either changes.
 *
 * Semantics:
 *   ink/neutral  — primary actions, text, most data series
 *   vermilion    — risk, danger, critical
 *   ochre        — warning, caution
 *   moss         — good, safe, clean
 *
 * Usage:
 * ```typescript
 * import { COLORS, BASIC_COLORS, STATUS_COLORS } from '../constants/colors';
 *
 * const buttonStyle = { background: COLORS.primary.DEFAULT };
 * const chartColor = BASIC_COLORS['Unsafe Driving'];
 * const statusStyle = STATUS_COLORS.excellent;
 * ```
 */

// ============================================================================
// PRIMARY PALETTE
// ============================================================================

/**
 * Main color palette - mirrors the Washi @theme scales in index.css
 */
export const COLORS = {
  primary: {
    DEFAULT: '#23211d',
    light: '#efebe1',
    dark: '#171512',
    50: '#f5f2ea',
    100: '#efebe1',
    200: '#e2ddd1',
    300: '#d3ccbc',
    400: '#a39d90',
    500: '#857f73',
    600: '#6d685e',
    700: '#4a463f',
    800: '#34312c',
    900: '#23211d',
  },
  success: {
    DEFAULT: '#5d7148',
    light: '#e2e7d5',
    dark: '#4b5c3a',
    50: '#eef0e6',
    100: '#e2e7d5',
    500: '#5d7148',
    600: '#5d7148',
    900: '#4b5c3a',
  },
  warning: {
    DEFAULT: '#b07d2b',
    light: '#efe3c8',
    dark: '#664911',
    50: '#f6efe0',
    100: '#efe3c8',
    500: '#b07d2b',
    600: '#9a6a22',
    900: '#664911',
  },
  danger: {
    DEFAULT: '#b3412c',
    light: '#f1e2da',
    dark: '#963524',
    50: '#f7ece6',
    100: '#f1e2da',
    500: '#b8513a',
    600: '#b3412c',
    900: '#963524',
  },
  gray: {
    50: '#f5f2ea',
    100: '#efebe1',
    200: '#e2ddd1',
    300: '#d3ccbc',
    400: '#a39d90',
    500: '#857f73',
    600: '#6d685e',
    700: '#4a463f',
    800: '#34312c',
    900: '#23211d',
  },
} as const;

// ============================================================================
// BASIC CATEGORY COLORS
// ============================================================================

/**
 * FMCSA BASIC category colors - used across analytics components.
 * Washi semantics, not a rainbow: vermilion = risk categories, ochre =
 * caution, ink/grays = informational.
 */
export const BASIC_COLORS: Record<string, string> = {
  // BASIC-11: Unsafe Driving — risk
  'Unsafe Driving': '#b3412c',
  'BASIC-11': '#b3412c',

  // BASIC-12: HOS Compliance — caution
  'Hours of Service': '#b07d2b',
  'HOS Compliance': '#b07d2b',
  'BASIC-12': '#b07d2b',

  // BASIC-13: Driver Fitness — informational
  'Driver Fitness': '#6d685e',
  'BASIC-13': '#6d685e',

  // BASIC-14: Controlled Substances/Alcohol — caution (deeper)
  'Controlled Substances': '#9a6a22',
  'Controlled Substances/Alcohol': '#9a6a22',
  'BASIC-14': '#9a6a22',

  // BASIC-15: Vehicle Maintenance — informational
  'Vehicle Maintenance': '#4a463f',
  'BASIC-15': '#4a463f',

  // BASIC-16: Crash Indicator — risk (deeper)
  'Crash Indicator': '#963524',
  'BASIC-16': '#963524',

  // BASIC-17: Hazardous Materials — informational (light)
  'Hazardous Materials': '#857f73',
  'Hazmat Compliance': '#857f73',
  'HM Compliance': '#857f73',
  'BASIC-17': '#857f73',

  // Fallback
  'Other': '#857f73',
};

// ============================================================================
// CHART COLORS
// ============================================================================

/**
 * Categorical Washi ramp for data visualizations.
 * Use for pie charts, bar charts, line charts, etc.
 */
export const CHART_COLORS = [
  '#23211d', // ink
  '#b3412c', // vermilion
  '#5d7148', // moss
  '#b07d2b', // ochre
  '#6d685e', // warm gray 600
  '#c7735a', // vermilion 400
  '#8a9a6a', // moss 400
  '#d3b574', // ochre 300
  '#a39d90', // warm gray 400
  '#34312c', // ink 800
] as const;

/**
 * Chart color scheme for time series (multiple lines)
 */
export const TIME_SERIES_COLORS = {
  violations: '#b3412c',
  inspections: '#4a463f',
  crashes: '#b07d2b',
  oos: '#963524',
  clean: '#5d7148',
} as const;

// ============================================================================
// STATUS COLORS
// ============================================================================

/**
 * Status colors for BASIC scores and risk levels
 * Provides background, text, and border colors for consistency
 */
export const STATUS_COLORS = {
  excellent: {
    bg: '#e2e7d5',
    text: '#4b5c3a',
    border: '#5d7148',
  },
  good: {
    bg: '#e2e7d5',
    text: '#4b5c3a',
    border: '#5d7148',
  },
  safe: {
    bg: '#efebe1',
    text: '#23211d',
    border: '#4a463f',
  },
  warning: {
    bg: '#efe3c8',
    text: '#664911',
    border: '#b07d2b',
  },
  critical: {
    bg: '#f1e2da',
    text: '#7a2b1e',
    border: '#b3412c',
  },
  insufficient: {
    bg: '#efebe1',
    text: '#6d685e',
    border: '#a39d90',
  },
  eliminated: {
    bg: '#efebe1',
    text: '#857f73',
    border: '#d3ccbc',
  },
} as const;

/**
 * Risk level colors for merger analysis
 */
export const RISK_COLORS = {
  LOW: {
    bg: '#e2e7d5',
    text: '#4b5c3a',
    badge: '#5d7148',
  },
  MEDIUM: {
    bg: '#efe3c8',
    text: '#664911',
    badge: '#b07d2b',
  },
  HIGH: {
    bg: '#f1e2da',
    text: '#7a2b1e',
    badge: '#b3412c',
  },
  CRITICAL: {
    bg: '#f7ece6',
    text: '#963524',
    badge: '#7a2b1e',
  },
} as const;

/**
 * Recommendation colors
 */
export const RECOMMENDATION_COLORS = {
  APPROVE: {
    bg: '#e2e7d5',
    text: '#4b5c3a',
    badge: '#5d7148',
  },
  REVIEW: {
    bg: '#efe3c8',
    text: '#664911',
    badge: '#b07d2b',
  },
  REJECT: {
    bg: '#f1e2da',
    text: '#7a2b1e',
    badge: '#b3412c',
  },
} as const;

// ============================================================================
// SEVERITY COLORS
// ============================================================================

/**
 * Violation severity colors
 */
export const SEVERITY_COLORS: Record<string, string> = {
  high: '#b3412c',
  medium: '#b07d2b',
  low: '#5d7148',
  '10': '#b3412c',
  '8': '#c7735a',
  '7': '#b07d2b',
  '5': '#b07d2b',
  '4': '#5d7148',
  '3': '#5d7148',
  '1': '#5d7148',
} as const;

// ============================================================================
// SAFETY RATING COLORS
// ============================================================================

/**
 * FMCSA safety rating colors
 */
export const SAFETY_RATING_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  S: { bg: '#e2e7d5', text: '#4b5c3a', border: '#5d7148' }, // Satisfactory
  C: { bg: '#efe3c8', text: '#664911', border: '#b07d2b' }, // Conditional
  U: { bg: '#f1e2da', text: '#7a2b1e', border: '#b3412c' }, // Unsatisfactory
  'Not Rated': { bg: '#efebe1', text: '#857f73', border: '#d3ccbc' },
} as const;

// ============================================================================
// DATA SOURCE COLORS
// ============================================================================

/**
 * Colors for data source indicators
 */
export const DATA_SOURCE_COLORS = {
  official: {
    bg: '#23211d',
    text: '#fbf9f4',
    badge: '#efebe1',
  },
  calculated: {
    bg: '#857f73',
    text: '#fbf9f4',
    badge: '#efebe1',
  },
  monitored: {
    bg: '#5d7148',
    text: '#fbf9f4',
    badge: '#e2e7d5',
  },
} as const;

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get status color by status name
 */
export const getStatusColor = (status: string): { bg: string; text: string; border: string } => {
  const normalized = status.toLowerCase();
  return STATUS_COLORS[normalized as keyof typeof STATUS_COLORS] || STATUS_COLORS.insufficient;
};

/**
 * Get BASIC category color by name or ID
 */
export const getBasicColor = (category: string): string => {
  return BASIC_COLORS[category] || BASIC_COLORS.Other;
};

/**
 * Get risk level color
 */
export const getRiskColor = (level: string): (typeof RISK_COLORS)[keyof typeof RISK_COLORS] => {
  return RISK_COLORS[level as keyof typeof RISK_COLORS] || RISK_COLORS.MEDIUM;
};

/**
 * Get recommendation color
 */
export const getRecommendationColor = (rec: string): (typeof RECOMMENDATION_COLORS)[keyof typeof RECOMMENDATION_COLORS] => {
  return RECOMMENDATION_COLORS[rec as keyof typeof RECOMMENDATION_COLORS] || RECOMMENDATION_COLORS.REVIEW;
};

export default COLORS;
